export const TRAVEL_RULE_DONE_PATH = "/travel-rule/done";

/**
 * The only things we keep off the fragment — never the PII that came with it.
 * `errorIdentifier` covers the hosted page's ERROR redirects: on a fatal error
 * (expired/invalid delegate token being the prod case, CT 1256059) the page
 * navigates here with `#message={"type":"error","identifier":"TOKEN_INVALID",…}`
 * instead of a result — the customer submitted NOTHING, so the done page must
 * not thank them. Our own backend bounce endpoint reuses the same shape with
 * `identifier: "LINK_EXPIRED"`. Only the identifier is kept; the free-text
 * message/description are dropped with the rest of the fragment.
 */
export type TravelRuleFragmentOutcome = { valid?: boolean; errorIdentifier?: string };

let outcome: TravelRuleFragmentOutcome | null = null;

/**
 * Notabene's hosted SafeConnect page redirects to `redirect_uri` with the
 * SafeConnect result **JSON-encoded in the URL fragment** (SDK README: "the user
 * will be redirected here on completion. The result parameters will be json
 * encoded in the URL fragment").
 *
 * That result is the same payload the backend callback unwraps, so it carries
 * IVMS101 counterparty PII — names, addresses, account numbers. This app
 * initialises Amplitude with `autocapture.pageViews` + `sessions` and a session
 * replay plugin (`Amplitude.ts`), and injects the third-party Adora recorder on
 * every route including public ones. All of those capture `location.href`, and
 * Amplitude's page-view event records it fragment and all. Left alone, a
 * customer completing a manual Travel Rule link would ship their counterparty
 * PII to two analytics vendors and leave it sitting in browser history.
 *
 * So: read the one field we actually want (`valid`), then drop the fragment via
 * `replaceState` so it never enters history and is gone before any recorder
 * initialises. Must be called from `index.tsx` ahead of `ReactDOM.render` —
 * `App` calls `initAmplitude()` in its component body, so bootstrap order is
 * what guarantees we win that race.
 *
 * Parsing is best-effort by design: the fragment's exact encoding isn't pinned
 * down by the SDK beyond that one README sentence, so anything unrecognised
 * simply yields no outcome and the page falls back to neutral copy. The strip
 * happens either way — that part must not depend on parsing succeeding.
 */
export function captureAndStripTravelRuleResult(): void {
  if (typeof window === "undefined") return;
  if (window.location.pathname !== TRAVEL_RULE_DONE_PATH) return;

  const raw = window.location.hash.replace(/^#/, "");
  if (raw) {
    outcome = parseOutcome(raw);
    // Drop the fragment whether or not we understood it. `replaceState` (not
    // `pushState`) so the PII-bearing URL leaves no history entry to go back to.
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
  }
}

/** Outcome recovered from the redirect fragment, if any was understood. */
export function getTravelRuleFragmentOutcome(): TravelRuleFragmentOutcome | null {
  return outcome;
}

function parseOutcome(raw: string): TravelRuleFragmentOutcome | null {
  const params = new URLSearchParams(raw);

  // Either the whole fragment is the JSON result, or it's `key=<json>` pairs.
  for (const candidate of [raw, ...Array.from(params.values())]) {
    const parsed = tryParseJson(candidate);
    if (!parsed || typeof parsed !== "object") continue;
    // Hosted-page fatal-error redirect (`#message={"type":"error",…}`) — the
    // flow never completed, so surface it. Identifier only; the free-text
    // fields are dropped with the fragment.
    if ((parsed as { type?: unknown }).type === "error") {
      const { identifier } = parsed as { identifier?: unknown };
      return { errorIdentifier: typeof identifier === "string" ? identifier : "UNKNOWN" };
    }
    if ("valid" in parsed) {
      const { valid } = parsed as { valid?: unknown };
      // Only trust an explicit boolean — never coerce.
      if (typeof valid === "boolean") return { valid };
    }
  }

  // In the key/value form only objects get JSON-stringified — scalars go through
  // `String(value)` (see `encodeObjectToFragment` in `notabene.service.ts`, our
  // mirror of the SDK's encoder). A boolean therefore arrives as the bare pair
  // `valid=false`, which has no JSON to parse and no enclosing object, so the
  // loop above can never match it. Read it directly, still without coercing:
  // only the two exact literals count.
  const flag = params.get("valid");
  if (flag === "true" || flag === "false") return { valid: flag === "true" };

  return null;
}

function tryParseJson(value: string): unknown {
  for (const candidate of [value, safeDecode(value)]) {
    if (!candidate) continue;
    try {
      return JSON.parse(candidate);
    } catch {
      // keep trying the other encoding
    }
  }
  return null;
}

function safeDecode(value: string): string | null {
  try {
    return decodeURIComponent(value);
  } catch {
    return null;
  }
}
